import { Image as ImageIcon } from 'lucide-react';
import * as React from 'react';

import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

/**
 * Parse a `/api/uploads/<id>` style URL and extract the attachment id + optional
 * variant query parameter. Returns null for external or non-upload URLs.
 */
export function parseUploadUrl(
  src: string | null | undefined,
): { id: string; variant?: string } | null {
  if (!src) return null;
  try {
    // URLs may be absolute or relative; anchor to current origin to parse cleanly.
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
    const url = new URL(src, base);
    if (!url.pathname.startsWith('/api/uploads/')) return null;
    const id = url.pathname.replace('/api/uploads/', '').split('/')[0];
    if (!id) return null;
    const variant = url.searchParams.get('variant') ?? undefined;
    return variant ? { id, variant } : { id };
  } catch {
    return null;
  }
}

interface AuthenticatedImageProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  attachmentId: string;
  variant?: string;
  fallbackClassName?: string;
}

/**
 * Fetches an attachment through the authenticated axios instance and renders
 * it as an `<img>` via a blob URL, so `Authorization` headers are included
 * (the server's `/api/uploads/:id` endpoint is behind `authMiddleware`).
 */
export function AuthenticatedImage({
  attachmentId,
  variant,
  className,
  alt,
  fallbackClassName,
  ...rest
}: AuthenticatedImageProps) {
  const [url, setUrl] = React.useState<string | null>(null);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setUrl(null);
    setFailed(false);
    api
      .get(`/uploads/${attachmentId}`, {
        params: variant ? { variant } : undefined,
        responseType: 'blob',
      })
      .then((res) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data as Blob);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachmentId, variant]);

  if (failed) {
    return (
      <span
        className={cn(
          'inline-flex items-center justify-center bg-muted text-muted-foreground',
          fallbackClassName ?? className,
        )}
      >
        <ImageIcon className="h-4 w-4" aria-hidden />
      </span>
    );
  }
  if (!url) {
    return <span className={cn('block animate-pulse bg-muted', className)} aria-hidden />;
  }
  return <img {...rest} src={url} alt={alt ?? ''} className={className} />;
}
